import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { WardService } from '../ward.service';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/material.module';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { IWard } from '../Iward';
import { catchError, finalize, of, Subject, takeUntil } from 'rxjs';
import { CapitalizeFirstLetterDirective } from 'src/app/common/directive/capitalize-first-letter.directive';

@Component({
  selector: 'app-ward-detail',
  standalone: true,
  imports: [
    CommonModule,
    TablerIconsModule,
    TranslateModule,
    MaterialModule,
    ReactiveFormsModule,
    CapitalizeFirstLetterDirective
  ],
  templateUrl: './ward-detail.component.html',
  styleUrl: './ward-detail.component.scss'
})
export class WardDetailComponent implements OnInit, OnDestroy {

  wardForm!: FormGroup;
  isEdit: boolean = false;
  isLoading: boolean = false;
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    private _wardService: WardService,
    private _formbuilder: FormBuilder,
    private dialogRef: MatDialogRef<WardDetailComponent>,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    @Inject(MAT_DIALOG_DATA) private _data: { ward: IWard, isEdit: boolean },
  ) {
    this.isEdit = _data?.isEdit;
  }

  ngOnInit(): void {
    this.wardForm = this._formbuilder.group({
      name: [this._data?.ward?.name || '', [Validators.required, Validators.maxLength(50)]],
      code: [this._data?.ward?.code || '', [Validators.maxLength(20)]],
      isActive: [this._data?.ward?.isActive ?? true]
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onAddWard() {
    if (this.wardForm.invalid) {
      return;
    }

    this.isLoading = true;
    const data = this.wardForm.getRawValue();

    this._wardService.createWard(data)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          this.toastr.error('Error occurred while creating ward');
          console.error('Error creating ward:', error);
          return of(null);
        })
      )
      .subscribe({
        next: (response) => {
          if (response?.isSuccess) {
            this.dialogRef.close(response);
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('ENTITY.WARD')));
          } else if (response) {
            const allErrors = Object.values(response.errors).flat();
            this.toastr.error(allErrors.join("\n"));
          }
        }
      });
  }

  onUpdateWard() {
    if (this.wardForm.invalid) {
      return;
    }

    this.isLoading = true;
    const data = {
      id: this._data?.ward?.id,
      ...this.wardForm.getRawValue()
    };

    this._wardService.updateWard(data)
      .pipe(
        takeUntil(this._unsubscribeAll),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.dialogRef.close(response);
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.UPDATE', this.translate.instant('ENTITY.WARD')));
          }
        },
        error: (error) => {
          if (error?.error?.errors) {
            const allErrors = Object.values(error.error.errors).flat();
            this.toastr.error(allErrors.join("\n"));
          } else {
            this.toastr.error('Something went wrong');
          }
        }
      });
  }

  onCloseDialog(): void {
    this.dialogRef.close();
  }

}
